import React, { useCallback, useEffect, useState } from 'react';
import Pushdown, {
    IPushdownComponentProps as IPushdownProps,
} from '../../components/Pushdown/pushdown';
import axios, { AxiosResponse } from 'axios';
import getPushdownError, {
    IFieldName,
    TErrorType,
} from './util/getPushdownError';
import {
    AcquisitionFlow,
    getAcquisitionFlow,
} from '../../utils/acquisition-utils';
import SessionStorage from '../../utils/SessionStorage';
import { generateMonthlyIncomeErrorMessage } from '../../utils/error-message';
import { isNewErrorMessagingFlagActive } from '../../utils/error-message-flag';

// error field comes from PushdownServlet.java
type PushdownServletResponse = IPushdownProps & { error?: string };

export type TNaturalDisaster = Omit<IPushdownProps, 'pushdownCode'> & { monthlyIncome?: string };

interface IPushdownProviderProps {
    children: React.ReactNode;
    naturalDisasterPushdown?: TNaturalDisaster;
}

export interface IPushdownContextValue {
    // TODO: remove in the future
    RenderPushdown: () => JSX.Element;
    showPushdown: (arg: string | TNaturalDisaster, codeOnly?: boolean) => void;
    closePushdown: () => void;
}

export const PushdownContext = React.createContext<IPushdownContextValue>({
    // eslint-disable-next-line react/display-name
    RenderPushdown: () => <></>,
    showPushdown: () => {},
    closePushdown: () => {},
});

export const PushdownProvider = (props: IPushdownProviderProps) => {
    const [usingPushdown, setUsingPushdown] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [pushdownContent, setPushdownContent] = useState<IPushdownProps>({
        header: '',
        description: '',
        message: [],
        pushdownCode: '',
        color: '',
    });

    const closePushdown = () => {
        setIsOpen(false);
    };

    const getPushdown = async (pushdown: string) => {
        if (!pushdown) {
            pushdown = 'undefined';
        }
        const url = !pushdown.includes('/content')
            ? `/services/pushdown.code=${pushdown}.json`
            : `/services/pushdown.contentPath=${pushdown.replace(
                'jcr:content',
                '_jcr_content',
            )}.json`;

        try {
            const response = await axios.get<PushdownServletResponse>(url);
            let data = null;
            if (typeof response.data === 'object') {
                data = response.data as PushdownServletResponse;
            } else if (typeof response.data === 'string') {
                try {
                    data = JSON.parse(response.data) as PushdownServletResponse;
                } catch (jsonError) {
                    getPushdown('undefined');
                    console.error(jsonError);
                }
            }

            if (data?.error) {
                throw { data };
            }

            if (data && (data.header || data.message || data.description)) {
                setPushdownContent({
                    header: data.header ?? '',
                    message: data.message ?? [],
                    description: data.description ?? '',
                    pushdownCode: data.pushdownCode ?? '',
                    color: data.color ?? '',
                });
            }
        } catch (err) {
            if (
                pushdown !== 'undefined' &&
                (err as AxiosResponse<PushdownServletResponse, any>)?.data
                    ?.error
            ) {
                getPushdown('undefined');
            } else {
                setPushdownContent({
                    header: 'Server Connection Lost',
                    message: [],
                    description:
                        '<p>Connection to our secure server has been lost and we are unable to continue processing your request. You will need to start over.</p>',
                    pushdownCode: '',
                    color: 'warning',
                });
            }
            console.error(err);
        }
    };

    const showPushdown = (arg: string | TNaturalDisaster, homeAddressPrimary?: boolean) => {
        if (isOpen) {
            closePushdown();
        }
        if (!arg || typeof arg === 'string') {
            getPushdown(arg as string).then(() => {
                setIsOpen(true);
            });
        } else if (arg) {
            if (arg.fieldErrors) {
                arg.message = arg.fieldErrors.map(([errorField, errorType]) => {
                    const retrievedPushdown = getValidationPushdown(
                        errorField as IFieldName,
                        errorType as TErrorType,
                        homeAddressPrimary,
                    );
                    if (errorField === 'MonthlyIncome' && isNewErrorMessagingFlagActive && arg.monthlyIncome !== undefined) {
                        retrievedPushdown.htmlError = generateMonthlyIncomeErrorMessage(
                            '<a href="#monthlyIncome-field-container" id="pushdown-monthlyIncome">Total Monthly Income</a>',
                            arg.monthlyIncome,
                        );
                    }
                    if (
                        errorField === 'MonthlyIncome' &&
                        (SessionStorage.tryGetObject('inc', [''])[0]) === 'yearly'
                    ) {
                        return (
                            retrievedPushdown?.htmlError ||
                            retrievedPushdown?.ErrorMessage
                        ).replaceAll('Monthly', 'Yearly');
                    }
                    return (
                        retrievedPushdown?.htmlError ||
                        retrievedPushdown?.ErrorMessage
                    );
                });
            }
            setPushdownContent({
                header: arg.header,
                message: arg.message,
                description: arg.description,
                pushdownCode: '',
                color: arg.color,
            });
            setIsOpen(true);
        }
    };

    useEffect(() => {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('errorCode')) {
            return;
        }
        const code = url.searchParams.get('errorCode');
        showPushdown(code as string);
        if (
            (code === 'noTncError' && getAcquisitionFlow() === AcquisitionFlow.PQ)
            || code !== 'noTncError' 
            || getAcquisitionFlow() !== AcquisitionFlow.PQ                        
        ) {
            url.searchParams.delete('errorCode');
            window.history.pushState(
                null,
                '',
                window.location.pathname + url.search,
            );
        }
    }, []);

    const getValidationPushdown = useCallback(getPushdownError, []);

    const RenderPushdown = useCallback(() => {
        setUsingPushdown(true);
        return <Pushdown isOpen={isOpen} {...pushdownContent} />;
    }, [pushdownContent, isOpen]);

    return (
        <PushdownContext.Provider
            value={{
                RenderPushdown,
                showPushdown,
                closePushdown,
            }}
        >
            {!usingPushdown && (
                <Pushdown isOpen={isOpen} {...pushdownContent} />
            )}
            {props.children}
        </PushdownContext.Provider>
    );
};

export const usePushdownContext = () => React.useContext(PushdownContext);
